import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { obtenerHistorialEdicion } from '../services/pruebasService';

// Campos que no son relevantes para mostrar en el diff
const CAMPOS_IGNORADOS = new Set([
  'id', 'pedido_id', 'created_at', 'updated_at',
  'ROW_ID', 'row_id', 'PRUEBA', 'PEDIDO', 'ACEPTAR', 'ACEPTADO', 'RAZON_RECHAZO',
  '_EDIT_MODE', 'nomId',
  'superviso', 'SUPERVISO',   // nunca mostrar supervisó
  'autorizo', 'AUTORIZO',     // nunca mostrar autorizó
  'usuario',     // campo interno que guarda el supervisor en varios formularios
  'nombre',      // auto-poblado, no es edición del usuario
  'opcion_nmx',  // auto-poblado al cargar el formulario
  'EDITADO', 'editado', 'EDITADO_POR', 'editado_por',
  'folioEmpresa', 'folio_empresa',
]);

// Etiquetas legibles para los campos más comunes
const ETIQUETAS: Record<string, string> = {
  fecha: 'Fecha',
  hora: 'Hora',
  grados: 'Temperatura (°C)',
  hr: 'Humedad relativa (%)',
  numero_solicitud: 'No. Solicitud',
  realizo: 'Realizó',
  observaciones: 'Observaciones',
  promedio: 'Promedio',
  redondeo: 'Redondeo',
  termo: 'Termohigrómetro',
  resultado: 'Resultado',
  // NMX-A-083
  folio: 'Folio',
  temperatura: 'Temperatura (°C)',
  humedad: 'Humedad relativa (%)',
  id_termohigrometro: 'ID Termohigrómetro',
  especificacion: 'Especificación',
  prenda: 'Prenda',
  dimensiones: 'Dimensiones',
  confeccion: 'Confección / Diseño',
  etiquetado: 'NOM-004-SE-2021 — Inspección del Etiquetado — Prueba no acreditada',
};

function etiqueta(campo: string): string {
  return ETIQUETAS[campo] || campo.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatVal(v: any): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) {
    if (v.length === 0) return '(sin filas)';
    return `${v.length} fila(s)`;
  }
  return String(v);
}

function valKey(v: any): string {
  if (Array.isArray(v)) return JSON.stringify(v);
  if (v === null || v === undefined || v === '') return '';
  return String(v);
}

interface RegistroEdicion {
  id: string;
  prueba_tabla: string;
  row_id: number;
  datos_antes: Record<string, any>;
  datos_despues: Record<string, any>;
  editado_por: string;
  created_at: string;
}

function DiffRow({ campo, antes, despues }: { campo: string; antes: any; despues: any }) {
  const cambiado = valKey(antes) !== valKey(despues);
  const esArray = Array.isArray(antes) || Array.isArray(despues);
  return (
    <>
      <tr className={cambiado ? 'bg-yellow-50' : ''}>
        <td className="px-3 py-1.5 text-xs text-muted-foreground font-medium w-1/3 border-b">{etiqueta(campo)}</td>
        <td className={`px-3 py-1.5 text-xs border-b w-1/3 ${cambiado ? 'text-red-700 line-through' : ''}`}>
          {formatVal(antes)}
        </td>
        <td className={`px-3 py-1.5 text-xs border-b w-1/3 ${cambiado ? 'text-green-700 font-semibold' : ''}`}>
          {formatVal(despues)}
        </td>
      </tr>
      {esArray && cambiado && (() => {
        const ant: any[] = Array.isArray(antes) ? antes : [];
        const des: any[] = Array.isArray(despues) ? despues : [];
        const maxLen = Math.max(ant.length, des.length);
        const filasDiff = Array.from({ length: maxLen }, (_, i) => {
          const a = ant[i];
          const d = des[i];
          if (JSON.stringify(a) === JSON.stringify(d)) return null;
          return { i, a, d };
        }).filter(Boolean);
        if (filasDiff.length === 0) return null;
        return (
          <tr className="bg-yellow-50/60">
            <td colSpan={3} className="px-6 pb-2 pt-0 border-b">
              <div className="space-y-1 mt-1">
                {filasDiff.map(({ i, a, d }: any) => (
                  <div key={i} className="text-[10px] text-muted-foreground">
                    <span className="font-medium text-foreground">Fila {i + 1}:</span>{' '}
                    <span className="text-red-600 line-through">{a ? JSON.stringify(a) : '(nueva)'}</span>
                    {' → '}
                    <span className="text-green-700 font-medium">{d ? JSON.stringify(d) : '(eliminada)'}</span>
                  </div>
                ))}
              </div>
            </td>
          </tr>
        );
      })()}
    </>
  );
}

function RegistroEdicionView({ registro }: { registro: RegistroEdicion }) {
  const antes = registro.datos_antes || {};
  const despues = registro.datos_despues || {};

  // Unión de todos los campos relevantes
  const campos = Array.from(
    new Set([...Object.keys(antes), ...Object.keys(despues)])
  ).filter(c => !CAMPOS_IGNORADOS.has(c));

  // Solo mostrar campos que cambiaron o tienen valor
  const camposConValor = campos.filter(c => {
    const a = formatVal(antes[c]);
    const d = formatVal(despues[c]);
    return a !== '—' || d !== '—';
  });

  const camposCambiados = camposConValor.filter(c => valKey(antes[c]) !== valKey(despues[c]));
  const camposSinCambio = camposConValor.filter(c => valKey(antes[c]) === valKey(despues[c]));

  const fecha = new Date(registro.created_at).toLocaleString('es-MX', {
    dateStyle: 'medium', timeStyle: 'short'
  });

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-orange-50 border-b px-4 py-2 flex items-center gap-2">
        <History className="w-4 h-4 text-orange-600" />
        <span className="text-sm font-medium text-orange-800">
          Editado por <span className="font-bold">{registro.editado_por || '—'}</span> el {fecha}
        </span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-3 py-2 text-left text-xs text-muted-foreground font-medium border-b">Campo</th>
            <th className="px-3 py-2 text-left text-xs text-muted-foreground font-medium border-b">Antes</th>
            <th className="px-3 py-2 text-left text-xs text-muted-foreground font-medium border-b">Después</th>
          </tr>
        </thead>
        <tbody>
          {camposCambiados.length === 0 ? (
            <tr>
              <td colSpan={3} className="px-3 py-3 text-xs text-center text-muted-foreground">
                No se detectaron cambios en los campos registrados.
              </td>
            </tr>
          ) : (
            <>
              {camposCambiados.map(campo => (
                <DiffRow key={campo} campo={campo} antes={antes[campo]} despues={despues[campo]} />
              ))}
              {camposSinCambio.length > 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-1.5 text-xs text-muted-foreground italic border-b">
                    {camposSinCambio.length} campo(s) sin cambios
                  </td>
                </tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}

interface HistorialEdicionModalProps {
  open: boolean;
  onClose: () => void;
  nomId: string;
  pruebaNombre: string;
}

export function HistorialEdicionModal({ open, onClose, nomId, pruebaNombre }: HistorialEdicionModalProps) {
  const [historial, setHistorial] = useState<RegistroEdicion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !nomId) return;
    setLoading(true);
    setError(null);
    obtenerHistorialEdicion(nomId)
      .then(data => setHistorial(data as RegistroEdicion[]))
      .catch(err => setError(err.message || 'Error al cargar historial'))
      .finally(() => setLoading(false));
  }, [open, nomId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-orange-600" />
            Historial de ediciones
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{pruebaNombre}</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading && (
            <div className="py-8 text-center text-muted-foreground text-sm animate-pulse">Cargando historial...</div>
          )}
          {error && (
            <div className="py-4 text-center text-red-600 text-sm">{error}</div>
          )}
          {!loading && !error && historial.length === 0 && (
            <div className="py-8 text-center text-muted-foreground text-sm">
              No hay ediciones registradas para esta prueba.
            </div>
          )}
          {!loading && !error && historial.length > 0 && (
            <div className="space-y-4">
              {historial.map(registro => (
                <RegistroEdicionView key={registro.id} registro={registro} />
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
